import { CART_KEY } from '@/lib/constants';
import type { ICartProduct } from '@/types';
import { checkIsLoggedIn } from '@/store/authorization-atom';
import {
  safeLocalStorageGetItem,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
} from '@/lib/safe-storage';

import { CART_SKIP_CROSS_DEVICE_MERGE_ONCE_KEY } from './cart-sync.constants';
import { postCartSync } from './cart-sync-api';
import { serializeCartForSync } from './serialize-cart-for-sync';

/**
 * After login: push anonymous/local cart to server (local wins).
 * If the browser has an **empty** cart, do **not** POST — that would replace the server cart with
 * nothing and wipe lines synced from another device. Cross-device merge then runs in
 * `CartSyncBootstrap` (GET /cart + LWW).
 *
 * Callers should `await` this **after** `setToken` / credentials and **before** `setAuthorized(true)` so
 * `CartSyncBootstrap` does not GET an empty cart while this POST is still in flight (production race).
 */
export async function runPostLoginCartSync(): Promise<void> {
  if (typeof window === 'undefined' || !checkIsLoggedIn()) return;
  let items: ICartProduct[] = [];
  try {
    const raw = safeLocalStorageGetItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw).items ?? [] : [];
    items = Array.isArray(parsed) ? parsed : [];
  } catch {
    return;
  }
  if (items.length === 0) {
    return;
  }
  try {
    await postCartSync(serializeCartForSync(items));
    try {
      // Skip one bootstrap merge only after local snapshot is successfully persisted.
      safeSessionStorageSetItem(CART_SKIP_CROSS_DEVICE_MERGE_ONCE_KEY, '1');
    } catch {
      /* ignore */
    }
  } catch {
    // Ensure a failed login sync never leaves a stale skip flag behind.
    safeSessionStorageRemoveItem(CART_SKIP_CROSS_DEVICE_MERGE_ONCE_KEY);
  }
}
